/**
 * Authentication Service
 * Handles user authentication using httpOnly cookies (secure against XSS)
 * No longer stores tokens in localStorage - all auth is cookie-based
 */

interface User {
  id: string;
  provider: string;
  email: string;
  name: string;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

class AuthService {
  private apiUrl: string;

  constructor() {
    // API URL is proxied through Vite dev server or Nginx in production
    this.apiUrl = '/api';
  }

  /**
   * Check if user is authenticated using lightweight /auth/check endpoint
   * This relies on httpOnly cookies being sent automatically
   * Returns true if authenticated, false otherwise (never throws on rate limit)
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const response = await fetch('/auth/check', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: send cookies
      });

      // Handle rate limiting - throw error, don't assume auth state
      if (response.status === 429) {
        console.error('Auth check rate limited');
        throw new Error('Too many requests. Please wait a moment.');
      }

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.authenticated === true;
    } catch (error) {
      console.error('Auth check error:', error);
      // Security: On error, assume NOT authenticated (redirect loop prevention handles this)
      return false;
    }
  }

  /**
   * Fetch current user information from API
   * Cookies are sent automatically with credentials: 'include'
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await fetch(`${this.apiUrl}/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired - try to refresh
          const refreshed = await this.refreshToken();
          if (refreshed) {
            // Retry after refresh
            return this.getCurrentUser();
          }
        }
        throw new Error('Failed to fetch user info');
      }

      const user = await response.json();
      return user;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  /**
   * Refresh access token using refresh token (automatic rotation)
   */
  async refreshToken(): Promise<boolean> {
    try {
      const response = await fetch('/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        console.log('Token refreshed successfully');
        return true;
      }

      console.error('Token refresh failed:', response.status);
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  /**
   * Logout user - blacklists token and clears cookies
   */
  async logout(): Promise<void> {
    try {
      const response = await fetch('/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('Logout endpoint returned error:', response.status);
      }
    } catch (error) {
      console.error('Logout API error:', error);
    }
  }

  /**
   * Initiate Google OAuth flow
   */
  loginWithGoogle(): void {
    window.location.href = '/auth/google';
  }

  /**
   * Initiate GitHub OAuth flow
   */
  loginWithGithub(): void {
    window.location.href = '/auth/github';
  }

  /**
   * Handle OAuth callback with authorization code from URL
   * Exchanges code for httpOnly cookies
   */
  async handleOAuthCallback(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');

    if (error) {
      console.error('OAuth error:', error);
      return false;
    }

    if (code) {
      try {
        // Exchange authorization code for tokens (set as httpOnly cookies)
        const response = await fetch('/auth/exchange', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });

        if (!response.ok) {
          console.error('Auth code exchange failed:', response.status);
          return false;
        }

        // Clean URL (remove code parameter)
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        window.history.replaceState({}, '', url.toString());

        return true;
      } catch (error) {
        console.error('Auth code exchange error:', error);
        return false;
      }
    }

    return false;
  }

  /**
   * Sign up with email and password
   */
  async signup(email: string, password: string, name: string): Promise<{ success: boolean; error?: string; user?: User }> {
    try {
      const response = await fetch('/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password, name }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Signup failed' };
      }

      // Tokens are now in httpOnly cookies - no need to store anything
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<{ success: boolean; error?: string; user?: User }> {
    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Login failed' };
      }

      // Tokens are now in httpOnly cookies - no need to store anything
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /**
   * Setup automatic token refresh before expiration
   * Call this after successful login/auth
   */
  setupAutoRefresh(): void {
    // Refresh token every 12 minutes (access token expires in 15 minutes)
    const refreshInterval = 12 * 60 * 1000; // 12 minutes

    setInterval(async () => {
      const refreshed = await this.refreshToken();
      if (!refreshed) {
        console.warn('Auto-refresh failed - user may need to re-login');
        // Don't redirect automatically - let the user continue until their session actually expires
      }
    }, refreshInterval);
  }

  /**
   * Get user statistics for account settings page
   */
  async getUserStats(): Promise<{
    total_minutes: number;
    total_sessions: number;
    member_since: string | null;
    last_login: string | null;
    credit_balance: number;
    can_claim_daily: boolean;
    provider: string;
  } | null> {
    try {
      const response = await fetch(`${this.apiUrl}/user/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          const refreshed = await this.refreshToken();
          if (refreshed) {
            return this.getUserStats();
          }
        }
        throw new Error('Failed to fetch user stats');
      }

      return await response.json();
    } catch (error) {
      console.error('Get user stats error:', error);
      return null;
    }
  }

  /**
   * Change password (email users only)
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/user/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to change password');
    }
  }

  /**
   * Check if user needs to accept terms
   * Returns terms status including whether acceptance is required and current version
   */
  async getTermsStatus(): Promise<{
    needs_acceptance: boolean;
    current_version: string;
    user_accepted_version: string | null;
  } | null> {
    try {
      const response = await fetch(`${this.apiUrl}/terms/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          const refreshed = await this.refreshToken();
          if (refreshed) {
            return this.getTermsStatus();
          }
        }
        throw new Error('Failed to fetch terms status');
      }

      return await response.json();
    } catch (error) {
      console.error('Get terms status error:', error);
      return null;
    }
  }

  /**
   * Accept terms of service
   * @param version The version of terms being accepted
   */
  async acceptTerms(version: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.apiUrl}/terms/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ version }),
      });

      if (!response.ok) {
        const data = await response.json();
        return { success: false, error: data.error || 'Failed to accept terms' };
      }

      return { success: true };
    } catch (error) {
      console.error('Accept terms error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

}

// Export singleton instance
export default new AuthService();
